import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, from, catchError } from 'rxjs';
import { concatMap, switchMap } from 'rxjs/operators';

import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { BaseGuard } from './base.guard';

const ADMIN_CLAIM_NAME = 'extension_InfrestGroups';

@Injectable()
export class RoleGuard extends BaseGuard {
  constructor(
    @Inject(MSAL_GUARD_CONFIG) protected override msalGuardConfig: MsalGuardConfiguration,
    protected override msalBroadcastService: MsalBroadcastService,
    protected override authService: MsalService,
    protected override location: Location,
    protected override router: Router
  ) {
    super(msalGuardConfig, msalBroadcastService, authService, location, router);
  }

  override activateHelper(state?: RouterStateSnapshot, route?: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    let result = super.activateHelper(state, route);

    const expectedRoles: string[] = route ? route.data['expectedRoles'] : [];

    return result.pipe(
      switchMap(() => {
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
          activeAccount = this.authService.instance.getAllAccounts()[0];
        }

        // If claims are missing, try to acquire token silently
        if (!activeAccount?.idTokenClaims?.[ADMIN_CLAIM_NAME]) {
          if (activeAccount) {
            return from(
              this.authService
                .acquireTokenSilent({
                  account: activeAccount,
                  scopes: ['openid', 'profile', 'offline_access'] // add your API scopes if needed
                })
                .toPromise()
            ).pipe(
              switchMap(result => {
                this.authService.instance.setActiveAccount(result.account);
                const claims = result.idTokenClaims as any;
                if (!claims?.[ADMIN_CLAIM_NAME]) {
                  this.router.navigateByUrl('/unauthorized');
                  return of(false);
                }
                return of(this.checkRoles(claims, expectedRoles));
              }),
              catchError(err => {
                this.router.navigateByUrl('/unauthorized');
                return of(false);
              })
            );
          } else {
            this.router.navigateByUrl('/unauthorized');
            return of(false);
          }
        }

        // Claims are present, check roles
        return of(this.checkRoles(activeAccount.idTokenClaims, expectedRoles));
      })
    );
  }

  private checkRoles(claims: any, expectedRoles: string[]): boolean {
    const roles = (claims?.[ADMIN_CLAIM_NAME] as string)?.split(',').map(r => r.trim()) || [];
    const hasRequiredRole = expectedRoles.some(role => roles.includes(role));
    if (!hasRequiredRole) {
      this.router.navigateByUrl('/unauthorized');
    }
    return hasRequiredRole;
  }
}
